import {
  Button,
  buttonVariants,
} from "@arcadeai/design-system/components/ui/atoms/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@arcadeai/design-system/components/ui/atoms/card";
import { Link } from "@tanstack/react-router";
import { Check, Copy, ExternalLink, Store } from "lucide-react";
import { useCallback, useState } from "react";
import { toast } from "sonner";
import { useGetUserSourcesQuery } from "@/app/user-sources/api/hooks";
import { useResourceScope } from "@/lib/resource-context";

const ShareRow = ({ label, url }: { label: string; url: string }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = useCallback(async () => {
    await navigator.clipboard.writeText(url);
    setCopied(true);
    toast.success("End-user link copied");
    setTimeout(() => setCopied(false), 2000);
  }, [url]);

  return (
    <div className="space-y-1.5">
      <p className="font-medium text-sm">{label}</p>
      <div className="flex items-center gap-2">
        <code className="block flex-1 break-all rounded-md border border-primary/40 bg-background p-2.5 font-mono text-xs">
          {url}
        </code>
        <Button
          aria-label={`Copy end-user link for ${label}`}
          onClick={handleCopy}
          size="icon"
          variant="outline"
        >
          {copied ? (
            <Check className="size-4 text-brand-text" />
          ) : (
            <Copy className="size-4" />
          )}
        </Button>
        <a
          aria-label={`Open end-user dashboard for ${label}`}
          className={buttonVariants({ size: "icon", variant: "outline" })}
          href={url}
          rel="noopener"
          target="_blank"
        >
          <ExternalLink className="size-4" />
        </a>
      </div>
    </div>
  );
};

/**
 * The payoff link for operators: the end-user dashboard URL they hand to users
 * so they can sign in through a user source and self-serve the tools published
 * below. One link per active user source (each carries its own `user_source_id`).
 * Brand-green framed to match the end-user connect panel — the two ends of the
 * same flow.
 */
export const EnduserShareCard = () => {
  const scope = useResourceScope();
  const { data } = useGetUserSourcesQuery();
  const sources = (data?.data.items ?? []).filter(
    (source) => source.status === "active"
  );
  const origin = typeof window === "undefined" ? "" : window.location.origin;

  return (
    <Card className="border-primary/60 bg-primary/5 ring-1 ring-primary/20">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-brand-text">
          <Store className="size-5" />
          End-user dashboard
        </CardTitle>
        <p className="text-muted-foreground text-sm">
          Share this link so your users can sign in and self-serve the tools you
          publish below.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {sources.length === 0 ? (
          <p className="text-muted-foreground text-sm">
            Add an active user source to generate a shareable end-user link.{" "}
            <Link
              className="inline-flex items-center gap-1 text-brand-text hover:underline"
              params={{
                organizationId: scope.organizationId,
                projectId: scope.projectId,
              }}
              to="/orgs/$organizationId/projects/$projectId/user-sources"
            >
              <ExternalLink className="size-3.5" />
              Manage user sources
            </Link>
          </p>
        ) : (
          sources.map((source) => (
            <ShareRow
              key={source.id}
              label={source.name}
              url={`${origin}/eu?project_id=${scope.projectId}&user_source_id=${source.id}`}
            />
          ))
        )}
      </CardContent>
    </Card>
  );
};
